import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslations } from 'use-intl';
import type {
  MatchAnalysisInput,
  MatchDetailInput,
  MatchProblemInput,
  MatchSquadEntry,
} from '@shared/schemas';
import { daysBetween, todayISO } from '@shared/dates';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import {
  useAddProblem,
  useDeleteProblem,
  useEvents,
  useMatch,
  useMatchCandidates,
  useOrg,
  usePromoteProblem,
  useSaveMatchAnalysis,
  useSaveMatchDetail,
  useSaveMatchSquad,
  useSeasons,
  useTeams,
} from '@/app/queries';
import { useSession as useAuthSession } from '@/app/session';
import { formatDate, sheetName } from '@/lib/format';
import { calledUp } from '@/lib/callUp';
import { CallUpSheet } from '@/reports/CallUpSheet';
import { AiMatchObjectives } from '@/routes/matches/AiMatchObjectives';
import { MatchAttendance } from '@/routes/matches/MatchAttendance';
import { MinutesPlan } from '@/routes/matches/MinutesPlan';
import {
  canShareImage,
  exportReportImage,
  exportReportPdf,
  reportFilename,
  shareReportImage,
} from '@/reports/export';
import { Button, Spinner } from '@/ui/Button';
import { Field, SelectInput, TextArea, TextInput } from '@/ui/Field';
import { EmptyState, Notice, PageHeader, Panel } from '@/ui/Panel';
import { Section } from '@/ui/Section';
import { SheetNumber } from '@/ui/Sheet';
import { Chip } from '@/ui/Chip';
import { PaperPreview } from '@/ui/PaperPreview';

const ROLES = ['starter', 'bench', 'unavailable'] as const;
const PHASES = [
  'with_ball',
  'without_ball',
  'attacking_transition',
  'defensive_transition',
  'set_pieces',
] as const;

/**
 * One match, from preparation through to what it changes.
 *
 * The order of the sections is the order a coach actually works in: what we want
 * to see, who played, what we saw, and then — the part the product exists for —
 * what it showed and what that becomes.
 */
/**
 * A save that failed has to say so.
 *
 * These buttons used to call `void mutation.mutateAsync(...)`, which throws the
 * rejection away: the screen kept the coach's edits, so it looked saved, and the
 * squad was gone on the next reload. The mutation's own error state is the one
 * place that knows, so it is rendered next to the button that caused it.
 */
function SaveFailed({ when, message }: { when: boolean; message: string }) {
  if (!when) return null;
  return (
    <div className="mt-3">
      <Notice tone="risk">{message}</Notice>
    </div>
  );
}

export function Matches({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const teams = useTeams();
  const teamList = teams.data ?? [];
  const [teamId, setTeamId] = useState<string | null>(null);
  const activeTeamId = teamId ?? teamList[0]?.id ?? null;

  const today = todayISO();
  const events = useEvents(activeTeamId ?? undefined);
  const seasons = useSeasons(activeTeamId ?? undefined);
  const candidates = useMatchCandidates(activeTeamId ?? undefined);

  /**
   * Nearest first: what is coming, soonest at the top, and behind it what has
   * been played, most recent first. A season's fixtures listed newest-first put
   * next May above next Saturday, which is never the match being worked on.
   */
  const fixtures = useMemo(() => {
    const all = (events.data ?? []).filter((e) => e.kind === 'match' || e.kind === 'friendly');
    const upcoming = all
      .filter((e) => e.onDate >= today)
      .sort((a, b) => a.onDate.localeCompare(b.onDate));
    const played = all
      .filter((e) => e.onDate < today)
      .sort((a, b) => b.onDate.localeCompare(a.onDate));
    return [...upcoming, ...played];
  }, [events.data, today]);

  const [eventId, setEventId] = useState<string | null>(null);
  /**
   * Whichever match is closest, in either direction. The day after a game that
   * is yesterday's, which is the one being analysed; the day before one it is
   * tomorrow's, which is the one being prepared. A tie goes to the fixture ahead.
   */
  const nearestId = useMemo(() => {
    const days = (onDate: string) => Math.abs(daysBetween(today, onDate));
    return [...fixtures].sort((a, b) => days(a.onDate) - days(b.onDate))[0]?.id ?? null;
  }, [fixtures, today]);
  const activeEventId = eventId ?? nearestId;

  const match = useMatch(activeEventId);
  const saveDetail = useSaveMatchDetail(activeEventId ?? '');
  const saveSquad = useSaveMatchSquad(activeEventId ?? '');
  const saveAnalysis = useSaveMatchAnalysis(activeEventId ?? '');
  const addProblem = useAddProblem(activeEventId ?? '');
  const deleteProblem = useDeleteProblem();
  const promote = usePromoteProblem();

  const [detail, setDetail] = useState<MatchDetailInput>({});
  const [analysis, setAnalysis] = useState<MatchAnalysisInput>({});
  const [squad, setSquad] = useState<Map<string, MatchSquadEntry>>(new Map());
  const [matchRpe, setMatchRpe] = useState<number | null>(null);
  const [problem, setProblem] = useState<MatchProblemInput>({ title: '', priority: 1 });
  const [hydrated, setHydrated] = useState<string | null>(null);

  // The call-up. Whether to name the eleven is decided per match and not stored:
  // the safe answer is no, and a coach who means yes is looking at the sheet when
  // he decides it.
  const org = useOrg();
  const { user } = useAuthSession();
  const callUpRef = useRef<HTMLDivElement>(null);
  const [revealEleven, setRevealEleven] = useState(false);
  const [publishObjective, setPublishObjective] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  useEffect(() => {
    if (!match.data || hydrated === activeEventId) return;
    setDetail(match.data.detail ?? {});
    setAnalysis(match.data.analysis ?? {});
    setSquad(new Map(match.data.squad.map((s) => [s.playerId, s])));
    // The rating was being written and never read back, so it looked unsaved
    // every time the screen was opened again.
    setMatchRpe(match.data.detail?.matchRpe ?? null);
    setHydrated(activeEventId);
  }, [match.data, activeEventId, hydrated]);

  const setEntry = (playerId: string, patch: Partial<MatchSquadEntry>) =>
    setSquad((m) => {
      const next = new Map(m);
      const existing = next.get(playerId) ?? { playerId, role: 'unavailable' as const };
      next.set(playerId, { ...existing, ...patch });
      return next;
    });

  const starters = [...squad.values()].filter((s) => s.role === 'starter').length;

  const fixture = fixtures.find((f) => f.id === activeEventId);

  // Built from the selection on screen rather than from the saved squad, so the
  // sheet shows what the coach is choosing while he chooses it.
  const callUpGroups = useMemo(
    () =>
      calledUp(
        (candidates.data ?? []).map((p) => ({
          playerId: p.playerId,
          firstName: p.firstName,
          lastName: p.lastName,
          shirtNumber: p.shirtNumber,
          positionKey: p.positionKey,
          role: squad.get(p.playerId)?.role ?? 'unavailable',
        })),
        { revealEleven },
      ),
    [candidates.data, squad, revealEleven],
  );
  const callUpCount = callUpGroups.reduce((n, g) => n + g.players.length, 0);

  const callUpFile = (extension: string) =>
    reportFilename(
      [t('match.callUp'), teamList.find((x) => x.id === activeTeamId)?.name, fixture?.opponent],
      fixture?.onDate ?? today,
      extension,
    );

  const sendCallUp = async (how: 'share' | 'image' | 'pdf') => {
    if (!callUpRef.current) return;
    setSending(true);
    setSendError(null);
    try {
      if (how === 'pdf') await exportReportPdf(callUpRef.current, callUpFile('pdf'));
      else if (how === 'image') await exportReportImage(callUpRef.current, callUpFile('png'));
      else await shareReportImage(callUpRef.current, callUpFile('png'), t('match.callUp'));
    } catch {
      setSendError(t('report.failed'));
    } finally {
      setSending(false);
    }
  };

  if (teams.isLoading || events.isLoading) {
    return (
      <p className="flex items-center gap-2 text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }

  if (fixtures.length === 0) {
    return (
      <>
        <PageHeader
          title={t('match.title')}
          lead={t('match.lead')}
          help={localePath(locale, '/guide/match')}
        />
        <Panel title={t('match.title')}>
          <EmptyState title={t('match.empty')} body={t('match.emptyBody')} />
        </Panel>
      </>
    );
  }

  const event = match.data?.event;
  const problems = match.data?.problems ?? [];

  /*
    Which part of the match week this fixture is in.

    The screen is a sequence — prepare it, play it, write what it showed — and
    stacking all seven panels open made it twelve and a half screens, most of
    them about a part of the week that has not happened. So each panel opens on
    the phase it belongs to and stays closed otherwise. A coach can still open
    any of them, and his choice is remembered.
  */
  const matchDate = event?.onDate ?? fixture?.onDate ?? today;
  const phase: 'before' | 'onTheDay' | 'after' =
    matchDate > today ? 'before' : matchDate < today ? 'after' : 'onTheDay';

  return (
    <>
      <PageHeader
        title={
          event
            ? `${formatDate(event.onDate, locale)} · ${event.opponent ?? t('calendar.kind_match')}`
            : t('match.title')
        }
        lead={t('match.lead')}
        help={localePath(locale, '/guide/match')}
      />

      <div className="mb-4 flex flex-wrap items-end gap-3">
        {teamList.length > 1 ? (
          <div className="flex flex-wrap gap-1">
            {teamList.map((tm) => (
              <button
                key={tm.id}
                type="button"
                onClick={() => {
                  setTeamId(tm.id);
                  setEventId(null);
                  setHydrated(null);
                }}
                aria-pressed={tm.id === activeTeamId}
                className={`rounded-(--radius-control) border px-3 py-1.5 text-meta transition-colors ${
                  tm.id === activeTeamId
                    ? 'border-ink bg-ink text-paper'
                    : 'border-line bg-surface text-ink-muted hover:border-ink'
                }`}
              >
                {tm.name}
              </button>
            ))}
          </div>
        ) : null}
        <div className="w-72">
          <Field label={t('match.pickMatch')}>
            {(p) => (
              <SelectInput
                {...p}
                value={activeEventId ?? ''}
                onChange={(e) => {
                  setEventId(e.target.value);
                  setHydrated(null);
                }}
              >
                {fixtures.map((f) => (
                  <option key={f.id} value={f.id}>
                    {formatDate(f.onDate, locale)} — {f.opponent ?? t('calendar.kind_match')}
                    {f.venue ? ` (${t(`calendar.venue_${f.venue}`)})` : ''}
                  </option>
                ))}
              </SelectInput>
            )}
          </Field>
        </div>
      </div>

      {match.isLoading ? (
        <p className="flex items-center gap-2 text-ink-muted">
          <Spinner /> {t('common.loading')}
        </p>
      ) : (
        <div className="grid gap-5">
          <Panel
            title={t('match.prepare')}
            collapsible
            storeKey="match.prepare"
            defaultOpen={phase === 'before'}
            summary={detail.whatWeWantToSee || undefined}
          >
            <Section title={t('match.whatWeWantToSee')} hint={t('match.whatWeWantToSeeHint')}>
              <TextArea
                value={detail.whatWeWantToSee ?? ''}
                onChange={(e) =>
                  setDetail((d) => ({ ...d, whatWeWantToSee: e.target.value || undefined }))
                }
              />
              <div className="mt-4 grid gap-x-5 gap-y-2 lg:grid-cols-2">
                <Field label={t('match.formation')} optional>
                  {(p) => (
                    <TextInput
                      {...p}
                      value={detail.formation ?? ''}
                      placeholder="4-2-3-1"
                      onChange={(e) =>
                        setDetail((d) => ({ ...d, formation: e.target.value || undefined }))
                      }
                    />
                  )}
                </Field>
                <div className="flex gap-3">
                  <Field label={t('match.goalsFor')} optional>
                    {(p) => (
                      <TextInput
                        {...p}
                        type="number"
                        min={0}
                        max={50}
                        value={detail.goalsFor ?? ''}
                        onChange={(e) =>
                          setDetail((d) => ({
                            ...d,
                            goalsFor: e.target.value === '' ? undefined : Number(e.target.value),
                          }))
                        }
                      />
                    )}
                  </Field>
                  <Field label={t('match.goalsAgainst')} optional>
                    {(p) => (
                      <TextInput
                        {...p}
                        type="number"
                        min={0}
                        max={50}
                        value={detail.goalsAgainst ?? ''}
                        onChange={(e) =>
                          setDetail((d) => ({
                            ...d,
                            goalsAgainst:
                              e.target.value === '' ? undefined : Number(e.target.value),
                          }))
                        }
                      />
                    )}
                  </Field>
                </div>
                {(
                  [
                    ['gamePlan', 'gamePlan'],
                    ['oppositionNotes', 'oppositionNotes'],
                    ['setPieces', 'setPieces'],
                  ] as const
                ).map(([field, key]) => (
                  <Field key={field} label={t(`match.${key}`)} optional>
                    {(p) => (
                      <TextArea
                        {...p}
                        value={(detail[field] as string | undefined) ?? ''}
                        onChange={(e) =>
                          setDetail((d) => ({ ...d, [field]: e.target.value || undefined }))
                        }
                      />
                    )}
                  </Field>
                ))}
              </div>
            </Section>
            <div className="border-t border-line px-4 py-4">
              <Button
                variant="primary"
                busy={saveDetail.isPending}
                onClick={() => void saveDetail.mutateAsync(detail)}
              >
                {t('common.save') === 'common.save' ? t('teams.save') : t('common.save')}
              </Button>
              <SaveFailed when={saveDetail.isError} message={t('common.saveFailed')} />
            </div>
          </Panel>

          <Panel
            title={t('match.squad')}
            count={t('match.starters', { count: starters })}
            collapsible
            storeKey="match.squad"
            defaultOpen={phase !== 'after'}
          >
            <Section title={t('match.matchRpe')} hint={t('match.matchRpeHint')}>
              <div className="flex flex-wrap gap-1.5">
                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                  <button
                    key={n}
                    type="button"
                    aria-pressed={matchRpe === n}
                    onClick={() => setMatchRpe(n)}
                    className={`tabular size-11 rounded-(--radius-control) border font-condensed text-subhead font-semibold transition-colors ${
                      matchRpe === n
                        ? 'border-ink bg-ink text-paper'
                        : 'border-line bg-surface text-ink-muted hover:border-ink'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
            </Section>

            <Section title={t('match.squad')}>
              <ul className="divide-y divide-line">
                {(candidates.data ?? []).map((p) => {
                  const entry = squad.get(p.playerId);
                  const role = entry?.role ?? 'unavailable';
                  return (
                    <li key={p.playerId} className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5">
                      <SheetNumber>{p.shirtNumber ?? '–'}</SheetNumber>
                      <span className="min-w-0 flex-1 truncate font-medium">
                        {sheetName(p.firstName, p.lastName)}
                      </span>
                      <div className="flex gap-1">
                        {ROLES.map((r) => (
                          <button
                            key={r}
                            type="button"
                            aria-pressed={role === r}
                            onClick={() => setEntry(p.playerId, { role: r })}
                            className={`min-h-11 min-w-11 rounded-(--radius-control) border px-3 text-meta transition-colors ${
                              role === r
                                ? 'border-ink bg-ink text-paper'
                                : 'border-line bg-surface text-ink-faint hover:border-ink hover:text-ink'
                            }`}
                          >
                            {t(`match.${r}`)}
                          </button>
                        ))}
                      </div>
                      {role !== 'unavailable' ? (
                        <TextInput
                          type="number"
                          min={0}
                          max={150}
                          aria-label={t('match.minutes')}
                          placeholder={t('match.minutes')}
                          className="w-24 !text-meta"
                          value={entry?.minutesPlayed ?? ''}
                          onChange={(e) =>
                            setEntry(p.playerId, {
                              minutesPlayed:
                                e.target.value === '' ? undefined : Number(e.target.value),
                            })
                          }
                        />
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            </Section>

            <div className="border-t border-line px-4 py-4">
              <Button
                variant="primary"
                busy={saveSquad.isPending}
                onClick={() =>
                  void saveSquad.mutateAsync({
                    entries: [...squad.values()].filter((s) => s.role !== 'unavailable'),
                    matchRpe: matchRpe ?? undefined,
                  })
                }
              >
                {t('match.saveSquad')}
              </Button>
              <SaveFailed when={saveSquad.isError} message={t('common.saveFailed')} />
            </div>
          </Panel>

          <Panel
            title={t('match.callUp')}
            count={t('match.calledUp', { count: callUpCount })}
            collapsible
            storeKey="match.callUp"
            defaultOpen={phase === 'before'}
          >
            <Section title={t('match.callUpDetails')} hint={t('match.callUpHint')}>
              <div className="grid gap-x-5 gap-y-2 lg:grid-cols-2">
                <Field label={t('match.meetAt')} optional>
                  {(p) => (
                    <TextInput
                      {...p}
                      type="time"
                      value={detail.meetAt ?? ''}
                      onChange={(e) =>
                        setDetail((d) => ({ ...d, meetAt: e.target.value || undefined }))
                      }
                    />
                  )}
                </Field>
                <Field label={t('match.meetPlace')} optional>
                  {(p) => (
                    <TextInput
                      {...p}
                      value={detail.meetPlace ?? ''}
                      onChange={(e) =>
                        setDetail((d) => ({ ...d, meetPlace: e.target.value || undefined }))
                      }
                    />
                  )}
                </Field>
                <Field label={t('match.kit')} optional>
                  {(p) => (
                    <TextInput
                      {...p}
                      value={detail.kit ?? ''}
                      onChange={(e) => setDetail((d) => ({ ...d, kit: e.target.value || undefined }))}
                    />
                  )}
                </Field>
                <Field label={t('match.callUpNote')} optional>
                  {(p) => (
                    <TextInput
                      {...p}
                      value={detail.callUpNote ?? ''}
                      onChange={(e) =>
                        setDetail((d) => ({ ...d, callUpNote: e.target.value || undefined }))
                      }
                    />
                  )}
                </Field>
              </div>

              <div className="mt-4 grid gap-2">
                <label className="flex items-start gap-2 text-meta text-ink-muted">
                  <input
                    type="checkbox"
                    checked={revealEleven}
                    onChange={(e) => setRevealEleven(e.target.checked)}
                    className="mt-0.5 size-4 accent-ink"
                  />
                  <span>
                    {t('match.revealEleven')}
                    <span className="block text-ink-faint">{t('match.revealElevenHint')}</span>
                  </span>
                </label>
                {detail.whatWeWantToSee ? (
                  <label className="flex items-start gap-2 text-meta text-ink-muted">
                    <input
                      type="checkbox"
                      checked={publishObjective}
                      onChange={(e) => setPublishObjective(e.target.checked)}
                      className="mt-0.5 size-4 accent-ink"
                    />
                    <span>
                      {t('match.publishObjective')}
                      <span className="block text-ink-faint">{t('match.publishObjectiveHint')}</span>
                    </span>
                  </label>
                ) : null}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-2">
                <Button
                  variant="secondary"
                  busy={saveDetail.isPending}
                  onClick={() => void saveDetail.mutateAsync(detail)}
                >
                  {t('common.save')}
                </Button>
                {canShareImage() ? (
                  <Button variant="primary" busy={sending} onClick={() => void sendCallUp('share')}>
                    {t('match.shareCallUp')}
                  </Button>
                ) : null}
                <Button
                  variant={canShareImage() ? 'secondary' : 'primary'}
                  busy={sending}
                  onClick={() => void sendCallUp('image')}
                >
                  {t('match.downloadImage')}
                </Button>
                <Button variant="quiet" busy={sending} onClick={() => void sendCallUp('pdf')}>
                  {t('report.export')}
                </Button>
                <Button variant="quiet" onClick={() => window.print()}>
                  {t('report.print')}
                </Button>
              </div>
              <SaveFailed when={saveDetail.isError} message={t('common.saveFailed')} />
              {sendError ? (
                <div className="mt-3">
                  <Notice tone="risk">{sendError}</Notice>
                </div>
              ) : null}
            </Section>

            {/* 120mm is 454px and the phone is 390. D-042 shaped this sheet
                like a message rather than a page precisely so a player can
                find his own name without opening anything — so showing two
                thirds of it was the one thing it could not afford. */}
            <PaperPreview className="report-preview border-t border-line bg-surface-sunken pb-4">
              <div ref={callUpRef} className="mx-auto w-fit p-4 shadow-(--shadow-sheet)">
                <CallUpSheet
                  meta={{
                    clubName: org.data?.name ?? '',
                    teamName: teamList.find((x) => x.id === activeTeamId)?.name,
                    coachName: user?.name,
                    logoUrl: org.data?.logoKey
                      ? `/api/org/logo?v=${encodeURIComponent(org.data.logoKey)}`
                      : null,
                    accent: org.data?.primaryColor,
                    issuedOn: formatDate(today, locale),
                  }}
                  fixture={{
                    opponent: fixture?.opponent,
                    dateLabel: formatDate(fixture?.onDate ?? today, locale),
                    kickOff: fixture?.startsAt,
                    venueLabel: fixture?.venue ? t(`calendar.venue_${fixture.venue}`) : null,
                    competition: fixture?.competition,
                  }}
                  details={{
                    meetAt: detail.meetAt,
                    meetPlace: detail.meetPlace,
                    kit: detail.kit,
                    note: detail.callUpNote,
                    whatWeWantToSee: publishObjective ? detail.whatWeWantToSee : null,
                  }}
                  groups={callUpGroups}
                  labels={{
                    title: t('match.callUp'),
                    coach: t('match.coach'),
                    versus: t('match.versus'),
                    meet: t('match.meet'),
                    kit: t('match.kit'),
                    starting: t('match.startingEleven'),
                    bench: t('match.bench'),
                    squad: t('match.squad'),
                    whatWeWantToSee: t('match.whatWeWantToSee'),
                    empty: t('match.callUpEmpty'),
                  }}
                />
              </div>
            </PaperPreview>
          </Panel>

          {activeEventId ? (
            <MatchAttendance eventId={activeEventId} openByDefault={phase === 'onTheDay'} />
          ) : null}

          {activeEventId ? (
            <MinutesPlan
              eventId={activeEventId}
              openByDefault={phase === 'onTheDay'}
              squad={candidates.data ?? []}
              teamName={teamList.find((x) => x.id === activeTeamId)?.name}
              locale={locale}
              baseFormation={detail.formation || '4-2-3-1'}
              fixture={{
                opponent: fixture?.opponent,
                onDate: fixture?.onDate ?? today,
                kickOff: fixture?.startsAt,
              }}
            />
          ) : null}

          <Panel
            title={t('match.analysis')}
            collapsible
            storeKey="match.analysis"
            defaultOpen={phase === 'after'}
          >
            <Section title={t('match.analysis')}>
              <div className="grid gap-x-5 gap-y-2 lg:grid-cols-2">
                {(
                  [
                    ['general', 'general'],
                    ['withBall', 'withBall'],
                    ['withoutBall', 'withoutBall'],
                    ['attackingTransition', 'attackingTransition'],
                    ['defensiveTransition', 'defensiveTransition'],
                    ['setPieces', 'setPieces'],
                    ['whatWorked', 'whatWorked'],
                    ['whatFailed', 'whatFailed'],
                  ] as const
                ).map(([field, key]) => (
                  <Field key={field} label={t(`match.${key}`)} optional>
                    {(p) => (
                      <TextArea
                        {...p}
                        value={(analysis[field] as string | undefined) ?? ''}
                        onChange={(e) =>
                          setAnalysis((a) => ({ ...a, [field]: e.target.value || undefined }))
                        }
                      />
                    )}
                  </Field>
                ))}
              </div>
            </Section>
            <div className="border-t border-line px-4 py-4">
              <Button
                variant="primary"
                busy={saveAnalysis.isPending}
                onClick={() => void saveAnalysis.mutateAsync(analysis)}
              >
                {t('match.saveAnalysis')}
              </Button>
              <SaveFailed when={saveAnalysis.isError} message={t('common.saveFailed')} />
            </div>
          </Panel>

          {/* The hinge. Everything above exists so this list is honest. */}
          <Panel
            title={t('match.problems')}
            count={problems.length || undefined}
            collapsible
            storeKey="match.problems"
            defaultOpen={phase === 'after'}
          >
            <p className="border-b border-line px-4 py-3 text-meta text-ink-muted">
              {t('match.promoteHint')}
            </p>

            {/* Suggestions join the list through the same endpoint the form
                uses, so there is still one way an objective is created. */}
            {activeEventId ? <AiMatchObjectives eventId={activeEventId} /> : null}

            {problems.length === 0 ? (
              <EmptyState title={t('match.noProblems')} body={t('match.noProblemsBody')} />
            ) : (
              <ul className="divide-y divide-line">
                {problems.map((p) => (
                  <li key={p.id} className="px-4 py-3">
                    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                      <SheetNumber>{p.priority}</SheetNumber>
                      <span className="min-w-0 flex-1 font-medium">{p.title}</span>
                      {p.phase ? <Chip muted>{t(`gameModel.phase_${p.phase}`)}</Chip> : null}
                      {p.objectiveId ? (
                        <Chip>{t('match.promoted')}</Chip>
                      ) : (
                        <Button
                          variant="secondary"
                          className="!text-meta"
                          busy={promote.isPending}
                          onClick={() =>
                            void promote.mutateAsync({
                              problemId: p.id,
                              input: {
                                teamId: activeTeamId!,
                                seasonId: seasons.data?.[0]?.id,
                                level: 'microcycle',
                              },
                            })
                          }
                        >
                          {t('match.promote')}
                        </Button>
                      )}
                      <Button
                        variant="quiet"
                        className="!px-2"
                        onClick={() => void deleteProblem.mutateAsync(p.id)}
                        aria-label={t('common.close')}
                      >
                        ×
                      </Button>
                    </div>
                    {p.detail ? (
                      <p className="mt-1 ps-11 text-meta text-ink-muted">{p.detail}</p>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}

            <Section title={t('match.addProblem')}>
              <div className="grid gap-x-5 gap-y-2 sm:grid-cols-[1fr_11rem_6rem_auto]">
                <TextInput
                  value={problem.title}
                  placeholder={t('match.problemTitle')}
                  onChange={(e) => setProblem((p) => ({ ...p, title: e.target.value }))}
                />
                <SelectInput
                  value={problem.phase ?? ''}
                  aria-label={t('match.phase')}
                  onChange={(e) =>
                    setProblem((p) => ({
                      ...p,
                      phase: (e.target.value || undefined) as typeof p.phase,
                    }))
                  }
                >
                  <option value="">{t('match.none')}</option>
                  {PHASES.map((ph) => (
                    <option key={ph} value={ph}>
                      {t(`gameModel.phase_${ph}`)}
                    </option>
                  ))}
                </SelectInput>
                <TextInput
                  type="number"
                  min={1}
                  max={20}
                  aria-label={t('match.priority')}
                  value={problem.priority}
                  onChange={(e) =>
                    setProblem((p) => ({ ...p, priority: Number(e.target.value) || 1 }))
                  }
                />
                <Button
                  variant="secondary"
                  disabled={!problem.title.trim()}
                  busy={addProblem.isPending}
                  onClick={async () => {
                    await addProblem.mutateAsync(problem);
                    setProblem({ title: '', priority: problems.length + 1 });
                  }}
                >
                  {t('match.addProblem')}
                </Button>
                <div className="sm:col-span-4">
                  <TextInput
                    value={problem.detail ?? ''}
                    placeholder={t('match.problemDetail')}
                    className="!text-meta"
                    onChange={(e) =>
                      setProblem((p) => ({ ...p, detail: e.target.value || undefined }))
                    }
                  />
                </div>
              </div>
            </Section>
          </Panel>
        </div>
      )}
    </>
  );
}
